/**
 * Pure transform: raw GraphQL user response → UserProfile shape.
 *
 * Shared between:
 * - useUserProfile hook (UserInsightsPage, embeds)
 * - UserManagementForm (already has raw data via getUser())
 *
 * Fields not yet available from GraphQL (datalake pipeline required)
 * are returned as null. See planner v17 readiness assessment.
 */
import type {
  OnlineBidderSignature,
  LiveBidderSignature,
  UaaBidderSignature,
  BidderSignature,
  PartyWalletAggregation,
  User,
} from '@adc/sdk/types'
import type { UserProfile } from '../types/userInsights'

// Subset of User fields returned by our USER_PROFILE_QUERY
export type RawUserProfileResponse = Pick<
  User,
  | 'user_id'
  | 'party_id'
  | 'first_name'
  | 'last_name'
  | 'email'
  | 'status'
  | 'is_current_user_remote_bid_qualified'
  | 'signature'
  | 'wallet'
>

// --- Helpers ---

function findSignature<T extends BidderSignature>(
  signatures: Array<BidderSignature | null | undefined> | null | undefined,
  typeName: T['__typename']
): T | undefined {
  return signatures?.find((s): s is T => s != null && s.__typename === typeName)
}

function getWalletAmount(
  aggregations: PartyWalletAggregation[] | null | undefined,
  paymentType: string
): number | null {
  const agg = aggregations?.find(a => a.payment_type === paymentType)
  return agg?.consumable_fund_amount ?? null
}

function getMostRecentDate(
  ...dates: (string | number | Date | null | undefined)[]
): string | null {
  const valid = dates
    .filter((d): d is string | number | Date => d != null)
    .map(d => new Date(d).getTime())
    .filter(t => !isNaN(t))

  if (valid.length === 0) return null
  return new Date(Math.max(...valid)).toISOString()
}

function deriveExperience(
  onlineSig: OnlineBidderSignature | undefined
): string {
  if (!onlineSig) return 'Unknown'

  const persona =
    onlineSig.staff_derived_persona ?? onlineSig.system_derived_persona
  if (persona) {
    // Format enum: EXPERIENCED_BUYER → Experienced Buyer
    return persona
      .split('_')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ')
  }

  if (onlineSig.current?.is_first_time_bidder) return 'First-Time'
  return 'Buyer'
}

// --- Computed metrics from bidder signatures ---

function computeTotalPurchases(
  onlineSig: OnlineBidderSignature | undefined,
  liveSig: LiveBidderSignature | undefined
): number | null {
  const onlineClosed = onlineSig?.historical?.contracts_closed_count ?? 0
  const liveClosed = liveSig?.historical?.contracts_closed_count ?? 0
  if (onlineClosed === 0 && liveClosed === 0 && !onlineSig && !liveSig) {
    return null
  }
  return onlineClosed + liveClosed
}

// --- Main transform ---

export function buildUserProfile(
  raw: RawUserProfileResponse,
  bannedListingCount = 0
): UserProfile {
  const signatures = raw.signature ?? []
  const onlineSig = findSignature<OnlineBidderSignature>(
    signatures,
    'OnlineBidderSignature'
  )
  const liveSig = findSignature<LiveBidderSignature>(
    signatures,
    'LiveBidderSignature'
  )
  const uaaSig = findSignature<UaaBidderSignature>(
    signatures,
    'UaaBidderSignature'
  )

  const walletAggs = raw.wallet?.aggregations ?? []

  return {
    user_id: raw.user_id ?? '',
    party_id: raw.party_id ?? null,
    first_name: raw.first_name ?? '',
    last_name: raw.last_name ?? '',
    email: raw.email ?? '',
    experience: deriveExperience(onlineSig),
    remote_approved: raw.is_current_user_remote_bid_qualified ?? false,
    status: raw.status ?? null,
    banned_listing_count: bannedListingCount,
    signup_date: uaaSig?.current?.user_created_date
      ? String(uaaSig.current.user_created_date)
      : null,
    last_purchase_date: getMostRecentDate(
      onlineSig?.historical?.latest_purchase_date,
      liveSig?.historical?.latest_purchase_date
    ),

    stats: {
      total_purchases: computeTotalPurchases(onlineSig, liveSig),
      purchases_ttm: null, // TODO: datalake aggregation pipeline
      participation_rate: null, // TODO: datalake aggregation pipeline
      active_registrations:
        onlineSig?.current?.auction_registration_count ?? null,
      active_bids: onlineSig?.current?.auctions_with_bids_count ?? null,
      active_closings: onlineSig?.current?.contracts_in_progress_count ?? null,
    },

    live_activity: {
      last_registration: liveSig?.historical?.latest_registration_date
        ? String(liveSig.historical.latest_registration_date)
        : null,
      last_bid: liveSig?.historical?.latest_bid_date
        ? String(liveSig.historical.latest_bid_date)
        : null,
      purchases_ttm: null, // TODO: datalake aggregation pipeline
      purchases_total: liveSig
        ? liveSig.historical?.contracts_closed_count ?? 0
        : null,
    },

    online_activity: {
      last_registration: onlineSig?.historical?.latest_registration_date
        ? String(onlineSig.historical.latest_registration_date)
        : null,
      last_bid: onlineSig?.historical?.latest_bid_date
        ? String(onlineSig.historical.latest_bid_date)
        : null,
      purchases_ttm: null, // TODO: datalake aggregation pipeline
      purchases_total: onlineSig
        ? onlineSig.historical?.contracts_closed_count ?? 0
        : null,
    },

    bidding_activity: {
      online_bids: onlineSig?.current?.auctions_with_bids_count ?? null,
      quality_bids:
        onlineSig?.current?.auctions_with_quality_bids_count ?? null,
      wallet_funds: getWalletAmount(walletAggs, 'ONLINE_BID_DEPOSIT'),
      remote_bid_available: getWalletAmount(walletAggs, 'LIVE_REMOTE_BIDDING'),
    },

    closing_activity: {
      contracts_in_progress:
        onlineSig?.current?.contracts_in_progress_count ?? null,
      avg_win_to_close_days: null, // TODO: requires backend aggregation (avg close_date - win_date per bidder)
      fallouts: onlineSig?.historical?.buyer_caused_fallout_count ?? null,
    },
  }
}
